import React, { useState, useEffect } from 'react'
import { 
  IdentificationIcon, 
  PlusIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

interface Licencia {
  id: string
  conductor: string
  cedula: string
  categoria: string
  numero: string
  fechaExpedicion: string
  fechaVencimiento: string
  estado: 'vigente' | 'vencida' | 'por_vencer' | 'suspendida'
  entidadExpedidora: string
  restricciones?: string[]
}

const PaginaLicencias: React.FC = () => {
  const [licencias, setLicencias] = useState<Licencia[]>([])
  const [busqueda, setBusqueda] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos')

  useEffect(() => {
    // Datos de ejemplo
    const licenciasEjemplo: Licencia[] = [
      {
        id: '1',
        conductor: 'Juan Carlos Pérez García',
        cedula: '12345678',
        categoria: 'C2',
        numero: 'LC123456789',
        fechaExpedicion: '2020-06-15',
        fechaVencimiento: '2025-06-15',
        estado: 'vigente',
        entidadExpedidora: 'RUNT - Ministerio de Transporte',
        restricciones: ['Uso de lentes']
      },
      {
        id: '2',
        conductor: 'María Elena García López',
        cedula: '87654321',
        categoria: 'C3',
        numero: 'LC987654321',
        fechaExpedicion: '2019-12-20',
        fechaVencimiento: '2024-12-20',
        estado: 'por_vencer',
        entidadExpedidora: 'RUNT - Ministerio de Transporte'
      },
      {
        id: '3',
        conductor: 'Carlos Alberto López Martínez',
        cedula: '11223344',
        categoria: 'C2',
        numero: 'LC112233445',
        fechaExpedicion: '2018-03-10',
        fechaVencimiento: '2024-03-10',
        estado: 'vencida',
        entidadExpedidora: 'RUNT - Ministerio de Transporte',
        restricciones: ['No conducir de noche']
      },
      {
        id: '4',
        conductor: 'Ana Patricia Rodríguez Silva',
        cedula: '55667788',
        categoria: 'C1',
        numero: 'LC556677889',
        fechaExpedicion: '2021-08-05',
        fechaVencimiento: '2026-08-05',
        estado: 'vigente',
        entidadExpedidora: 'RUNT - Ministerio de Transporte'
      }
    ]
    setLicencias(licenciasEjemplo)
  }, [])

  const licenciasFiltradas = licencias.filter(licencia => {
    const cumpleBusqueda = licencia.conductor.toLowerCase().includes(busqueda.toLowerCase()) ||
                          licencia.cedula.includes(busqueda) ||
                          licencia.numero.toLowerCase().includes(busqueda.toLowerCase())
    const cumpleFiltroEstado = filtroEstado === 'todos' || licencia.estado === filtroEstado
    const cumpleFiltroCategoria = filtroCategoria === 'todos' || licencia.categoria === filtroCategoria
    return cumpleBusqueda && cumpleFiltroEstado && cumpleFiltroCategoria
  })

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'vigente':
        return 'text-green-600 bg-green-100'
      case 'por_vencer':
        return 'text-yellow-600 bg-yellow-100'
      case 'vencida':
        return 'text-red-600 bg-red-100'
      case 'suspendida':
        return 'text-gray-600 bg-gray-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'vigente':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'por_vencer':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'vencida':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      case 'suspendida':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      default:
        return <CheckCircleIcon className="h-4 w-4" />
    }
  }

  const calcularDiasVencimiento = (fechaVencimiento: string) => {
    const fecha = new Date(fechaVencimiento)
    const hoy = new Date()
    const diferencia = Math.ceil((fecha.getTime() - hoy.getTime()) / (1000 * 3600 * 24))
    return diferencia
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Licencias de Conducción</h1>
            <p className="text-gray-600 dark:text-gray-400">Gestiona las licencias de conducción de los conductores</p>
          </div>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <PlusIcon className="h-5 w-5" />
            <span>Registrar Licencia</span>
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <IdentificationIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Licencias</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{licencias.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Vigentes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {licencias.filter(l => l.estado === 'vigente').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Por Vencer</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {licencias.filter(l => l.estado === 'por_vencer').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Vencidas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {licencias.filter(l => l.estado === 'vencida').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por conductor, cédula o número de licencia..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="todos">Todos los estados</option>
            <option value="vigente">Vigente</option>
            <option value="por_vencer">Por vencer</option>
            <option value="vencida">Vencida</option>
            <option value="suspendida">Suspendida</option>
          </select>
        </div>

        <div>
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="todos">Todas las categorías</option>
            <option value="C1">C1</option>
            <option value="C2">C2</option>
            <option value="C3">C3</option>
          </select>
        </div>
      </div>

      {/* Lista de licencias */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Conductor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Licencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Fechas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Restricciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {licenciasFiltradas.map((licencia) => (
                <tr key={licencia.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {licencia.conductor}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        CC: {licencia.cedula}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Categoría {licencia.categoria}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {licencia.numero}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {licencia.entidadExpedidora}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        Expedición: {new Date(licencia.fechaExpedicion).toLocaleDateString('es-ES')}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Vencimiento: {new Date(licencia.fechaVencimiento).toLocaleDateString('es-ES')}
                      </div>
                      {licencia.estado === 'por_vencer' && (
                        <div className="text-xs text-yellow-600">
                          {calcularDiasVencimiento(licencia.fechaVencimiento)} días restantes
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(licencia.estado)}`}>
                      {getEstadoIcon(licencia.estado)}
                      <span className="ml-1 capitalize">{licencia.estado.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {licencia.restricciones && licencia.restricciones.length > 0 ? (
                      <div className="space-y-1">
                        {licencia.restricciones.map((restriccion, index) => (
                          <span
                            key={index}
                            className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full mr-1"
                          >
                            {restriccion}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Sin restricciones</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300">
                        <DocumentTextIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {licenciasFiltradas.length === 0 && (
          <div className="text-center py-8">
            <IdentificationIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No hay licencias</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No se encontraron licencias con los criterios de búsqueda.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaLicencias
